import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import Card from '@mui/material/Card';
import Swal from 'sweetalert2';

import CardContent from '@mui/material/CardContent';
import { Backdrop, CircularProgress, Stack } from '@mui/material';

import ArgonBox from 'component/Argon/ArgonBox';
import ArgonButton from 'component/Argon/ArgonButton';
import DashboardLayout from 'layout/LayoutContainers/DashboardLayout';
import FormField from 'component/basicForm/FormField';
import ArgonTypography from 'component/Argon/ArgonTypography';

import { useUser } from 'api/admin/getUser';

import { generatePassword } from 'utils/pwdHelper';
import { useUpdateUserCredential, useUpdateUser } from 'api/admin/putUser';
import useAuth from 'auth/hook/useAuth';
import ROLES from 'auth/roles';

function AdminUser() {
  const { auth } = useAuth();
  const { id } = useParams();
  const { data, isLoading, isFetched } = useUser(id);
  const [pwd, setPwd] = useState('');
  const [d3m, setD3m] = useState('0');
  const [d6m, setD6m] = useState('0');
  const [d1y, setD1y] = useState('0');
  const [d15m, setD15m] = useState('0');
  const [credit, setCredit] = useState(0);

  const { mutateAsync: updatePwd } = useUpdateUserCredential(id);
  const { mutateAsync: updateUser } = useUpdateUser(id);

  useEffect(() => {
    if (data?.discount) {
      setD3m(data?.discount?.['3m']?.toString() || '0');
      setD6m(data?.discount?.['6m']?.toString() || '0');
      setD1y(data?.discount?.['1y']?.toString() || '0');
      setD15m(data?.discount?.['15m']?.toString() || '0');
    }
    if (data?.credit >= 0) {
      setCredit(data.credit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  const isAccess = useMemo(() => {
    if (auth?.roles) {
      if (auth?.roles?.find((x) => x > ROLES.Admin)) {
        return true;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.accessToken]);

  function handleSubmitPwd() {
    if (!pwd) {
      return;
    }
    const confirmSwal = Swal.mixin({
      customClass: {
        confirmButton: 'button button-success',
        cancelButton: 'button button-error',
      },
      buttonsStyling: false,
    });

    confirmSwal
      .fire({
        title: 'ต้องการเปลี่ยนรหัสผ่าน?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      })
      .then((result) => {
        if (result.isConfirmed) {
          updatePwd({ username: data?.email, newPassword: pwd })
            .then(() => {
              setPwd('');
            })
            .catch(console.error);
        }
      });
  }

  function handleUpdateCoupon() {
    const confirmSwal = Swal.mixin({
      customClass: {
        confirmButton: 'button button-success',
        cancelButton: 'button button-error',
      },
      buttonsStyling: false,
    });

    confirmSwal
      .fire({
        title: 'ยืนยัน?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      })
      .then((result) => {
        if (result.isConfirmed) {
          updateUser({
            discount: {
              '3m': d3m.toString(),
              '6m': d6m.toString(),
              '1y': d1y.toString(),
              '15m': d15m.toString(),
            },
          }).catch(console.error);
        }
      });
  }

  function handleUpdateCredit() {
    const confirmSwal = Swal.mixin({
      customClass: {
        confirmButton: 'button button-success',
        cancelButton: 'button button-error',
      },
      buttonsStyling: false,
    });

    confirmSwal
      .fire({
        title: 'ยืนยัน?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      })
      .then((result) => {
        if (result.isConfirmed) {
          updateUser({
            credit,
          }).catch(console.error);
        }
      });
  }

  if (isLoading) {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <DashboardLayout>
      <Card>
        <ArgonBox mx={3} mt={3}>
          <ArgonTypography variant="h5" fontWeight="medium">
            ข้อมูลผู้ใช้งาน
          </ArgonTypography>
        </ArgonBox>
        <CardContent>
          <ArgonBox component="form" role="form">
            <ArgonBox mb={2}>
              <FormField label="บริษัท" value={`${data?.contact?.company || ''}`} disabled />
            </ArgonBox>
            <ArgonBox mb={2}>
              <FormField label="ขื่อ" value={`${data?.contact?.name} ${data?.contact?.lastname || ''}`} disabled />
            </ArgonBox>
            <ArgonBox mb={2}>
              <FormField label="โทรศัพท์" value={`${data?.contact?.phone || ''} `} disabled />
            </ArgonBox>
            <ArgonBox mb={2}>
              <FormField
                label="ที่อยู่"
                value={`${data?.contact?.address1} ${data?.contact?.district} ${data?.contact?.area} ${data?.contact?.province} ${data?.contact?.zipcode}`}
                disabled
              />
            </ArgonBox>
            <ArgonBox mb={2}>
              <FormField label="อีเมล" value={data?.email} disabled />
            </ArgonBox>
            <ArgonBox mb={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FormField placeholder="รหัสผ่าน ********" value={pwd} onChange={(e) => setPwd(e.target.value)} />
                <ArgonButton
                  color="info"
                  size="large"
                  variant="gradient"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => {
                    setPwd(generatePassword(8));
                  }}
                >
                  สุ่มใหม่
                </ArgonButton>
              </Stack>
            </ArgonBox>
            <ArgonBox mt={4} mb={1}>
              <ArgonButton
                color="success"
                size="large"
                variant="gradient"
                onClick={handleSubmitPwd}
                disabled={pwd?.length < 8}
              >
                บันทึกรหัสผ่าน
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>
        </CardContent>
      </Card>
      {isAccess && (
        <>
          <Card sx={{ mt: 2.5 }}>
            <ArgonBox mx={3} mt={3}>
              <ArgonTypography variant="h5" fontWeight="medium">
                รายการส่วนลด
              </ArgonTypography>
            </ArgonBox>
            <CardContent>
              <ArgonBox component="form" role="form">
                <ArgonBox mb={2}>
                  <FormField label="3เดือน" value={d3m} onChange={(e) => setD3m(e.target.value)} />
                </ArgonBox>
                <ArgonBox mb={2}>
                  <FormField label="6เดือน" value={d6m} onChange={(e) => setD6m(e.target.value)} />
                </ArgonBox>
                <ArgonBox mb={2}>
                  <FormField label="1ปี" value={d1y} onChange={(e) => setD1y(e.target.value)} />
                </ArgonBox>
                <ArgonBox mb={2}>
                  <FormField label="15เดือน" value={d15m} onChange={(e) => setD15m(e.target.value)} />
                </ArgonBox>
                <ArgonBox mt={4} mb={1}>
                  <ArgonButton color="success" size="large" sx={{ whiteSpace: 'nowrap' }} onClick={handleUpdateCoupon}>
                    บันทึกส่วนลด
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </CardContent>
          </Card>
          <Card sx={{ mt: 2.5 }}>
            <ArgonBox mx={3} mt={3}>
              <ArgonTypography variant="h5" fontWeight="medium">
                เครดิต
              </ArgonTypography>
            </ArgonBox>
            <CardContent>
              <ArgonBox component="form" role="form">
                <ArgonBox mb={2}>
                  <FormField value={credit} onChange={(e) => setCredit(e.target.value)} />
                </ArgonBox>
                <ArgonBox mt={4} mb={1}>
                  <ArgonButton color="success" size="large" sx={{ whiteSpace: 'nowrap' }} onClick={handleUpdateCredit}>
                    บันทึก
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </CardContent>
          </Card>
        </>
      )}
    </DashboardLayout>
  );
}

export default AdminUser;
